const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Returns `next`, but with every subtree that is deeply equal to the matching
 * subtree of `prev` replaced by the `prev` reference. If nothing changed, `prev`
 * itself is returned. Only plain objects and arrays are compared structurally;
 * anything else is compared with `Object.is`.
 */
export const replaceEqualDeep = <T>(prev: unknown, next: T): T => {
  if (Object.is(prev, next)) return prev as T;
  const prevIsArray = Array.isArray(prev);
  const nextIsArray = Array.isArray(next);
  if (prevIsArray && nextIsArray) {
    const prevArray = prev as unknown[];
    const nextArray = next as unknown as unknown[];
    let equalItems = 0;
    const result = nextArray.map((item, index) => {
      const shared = replaceEqualDeep(prevArray[index], item);
      if (index < prevArray.length && shared === prevArray[index]) {
        equalItems++;
      }
      return shared;
    });
    return (
      prevArray.length === nextArray.length && equalItems === prevArray.length
        ? prev
        : result
    ) as T;
  }
  if (
    !prevIsArray &&
    !nextIsArray &&
    isPlainObject(prev) &&
    isPlainObject(next)
  ) {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    let equalItems = 0;
    const result: Record<string, unknown> = {};
    for (const key of nextKeys) {
      const shared = replaceEqualDeep(prev[key], next[key]);
      result[key] = shared;
      if (
        Object.prototype.hasOwnProperty.call(prev, key) &&
        shared === prev[key]
      ) {
        equalItems++;
      }
    }
    return (
      prevKeys.length === nextKeys.length && equalItems === prevKeys.length
        ? prev
        : result
    ) as T;
  }
  return next;
};
